import React, { memo, useMemo } from 'react'

import { classnames, Filter, Icon, MoreChorizontal } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { arrayOf, bool, func, number, oneOfType, shape, string } from 'prop-types'

import { iconSize, VARIATION_TYPES } from 'Pods/ThreeDStudio/constants'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import DeleteConfirmModal from 'Pods/ThreeDStudio/Shared/DeleteConfirmModal/DeleteConfirmModal'
import Dropdown from 'Pods/ThreeDStudio/Shared/Dropdown/Dropdown'

import { NOOP } from 'Utils/stableDefaults'

import Info from './Components/Info/Info'
import Media from './Components/Media/Media'
import Menu from './Components/Menu/Menu'
import useCardMenu from './useCardMenu'
import useOpenVariation from './useOpenVariation'

const triggerIconSize = { height: '36px', width: '36px' }
const triggerIcon = <Icon notSpaced svgComponent={MoreChorizontal} color="dark" size={triggerIconSize} />

function Card({
  variation,
  position = null,
  isActive = false,
  isMain = false,
  isActionLoading = false,
  canOpenRelatedTab = false,
  showInfo = false,
  onSetAsMain: handleSetAsMain = NOOP,
  onDelete: handleDelete = NOOP,
}) {
  const t = useTranslation()
  const { id, slug, providerModelId, outputAssets, title, prompt, createdAt } = variation
  const previewUrl = outputAssets?.[0]?.previewUrl
  const variationType = VARIATION_TYPES[providerModelId]
  const itemName = variationType?.label ?? title ?? prompt
  const { handleClick, handleKeyDown, handleOpenRelatedTab } = useOpenVariation({ id, slug, title: title ?? prompt })
  const {
    shouldCloseDropdown,
    setShouldCloseDropdown,
    isMenuOpen,
    setIsMenuOpen,
    isDeleteModalOpen,
    handleSetAsMainClick,
    handleOpenDelete,
    handleCloseDelete,
    handleConfirmDelete,
  } = useCardMenu({ id, itemName, onSetAsMain: handleSetAsMain, onDelete: handleDelete })

  const menuContent = useMemo(() =>
    <Menu
      createdAt={createdAt}
      onSetAsMain={handleSetAsMainClick}
      onDelete={handleOpenDelete}
      isDisabled={isActionLoading}
    />,
    [createdAt, handleSetAsMainClick, handleOpenDelete, isActionLoading],
  )

  const media = (
    <Media
      previewUrl={previewUrl}
      alt={title ?? prompt ?? variationType?.label ?? 'Variation preview'}
      variationType={variationType}
      isMain={isMain}
      position={position}
    >
      {canOpenRelatedTab && variationType && (
        <button
          type="button"
          className="variation-card__related-tab"
          aria-label={t('viewerSidebar.enhance.variations.openRelatedTab')}
          onClick={handleOpenRelatedTab}
        >
          <Icon notSpaced svgComponent={Filter} color="dark" size={iconSize} />
        </button>
      )}
      <Dropdown
        icon={triggerIcon}
        content={menuContent}
        placement="bottom-start"
        className="variation-card__dropdown-trigger"
        contentClassName="variation-card__dropdown-content"
        triggerCloseDropdown={shouldCloseDropdown}
        setTriggerCloseDropdown={setShouldCloseDropdown}
        onOpenChange={setIsMenuOpen}
        usePortal
      />
    </Media>
  )

  return (
    <>
      <div
        className={classnames(
          'variation-card',
          showInfo && 'variation-card--with-info',
          isActive && 'variation-card--active',
          isMenuOpen && 'variation-card--menu-open',
        )}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
      >
        {showInfo ? (
          <>
            <div className="variation-card__media">{media}</div>
            <Info variation={variation} />
          </>
        ) : media}
      </div>
      {isDeleteModalOpen && (
        <DeleteConfirmModal
          itemType="variation"
          itemName={itemName}
          isSubmitDisabled={isActionLoading}
          onSubmit={handleConfirmDelete}
          onCancel={handleCloseDelete}
        />
      )}
    </>
  )
}

Card.propTypes = {
  variation: shape({
    id: oneOfType([string, number]).isRequired,
    slug: string,
    providerModelId: string,
    outputAssets: arrayOf(shape({
      previewUrl: string,
    })),
    title: string,
    prompt: string,
    createdAt: string,
  }).isRequired,
  position: number,
  isActive: bool,
  isMain: bool,
  isActionLoading: bool,
  canOpenRelatedTab: bool,
  showInfo: bool,
  onSetAsMain: func,
  onDelete: func,
}

export default memo(Card)
